import React, { useState, useEffect, useMemo } from "react";

import Switch from "@mui/material/Switch";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchUsers, setIsAdmin, setIsImportantEmailReceiver } from "../../redux/actions/UserActions";
import { fetchRoles } from "../../redux/actions/RoleActions";
import { displayDateFormart } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import UserForm from "./UserForm";
import Loader from "../../components/loader/Loader";
import { UserModel } from "../../models/UserModel";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  firstName: string;
  lastName: string;
  emailId: string;
  createdDate: string;
  role: string;
};

const UserPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<UserModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);
  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User); // Add this line to get the logged-in user
  const allData = useAppSelector((state) => state.user.all_Users);
  const allRoles = useAppSelector((state) => state.role.all_Roles);
  const isUsersFetching = useAppSelector((state) => state.user.isFetching);

  const columns = useMemo(() => {
    return [
      {
        id: "firstName",
        numeric: false,
        disablePadding: false,
        label: "First Name",
        width: 150,
      },
      {
        id: "lastName",
        numeric: false,
        disablePadding: false,
        label: "Last Name",
        width: 200,
      },
      {
        id: "emailId",
        numeric: false,
        disablePadding: false,
        label: "Email",
        width: 150,
      },
      {
        id: "role",
        numeric: false,
        disablePadding: false,
        label: "Role",
        width: 150,
      },
      {
        id: "isAdmin",
        numeric: false,
        disablePadding: false,
        label: "Is Admin",
        width: 150,
      },
      {
        id: "isImportantEmailReceiver",
        numeric: false,
        disablePadding: false,
        label: "Is Important Email Receiver",
        width: 150,
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 200,
      },
    ];
  }, []);

  //To fetch all users and Roles
  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchRoles());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    const handleIsAdmin = async (userId: number, status: boolean) => {
      dispatch(setIsAdmin(userId, status));
    };
    const handleIsImportantEmailReceiver = async (userId: number, status: boolean) => {
      dispatch(setIsImportantEmailReceiver(userId, status));
    };
    setRootData(() =>
      allData.map((rec) => {
        const userRole = allRoles.find((uRole) => uRole.roleId === rec.roleId);
        const isAdminChecked =
          rec.roleId === 7 ? true : rec.isAdmin ? true : false;
        const isImportantEmailReceiverChecked = rec.isImportantEmailReceiver ? true : false;
        return {
          id: rec.userId,
          firstName: rec.firstName,
          lastName: rec.lastName,
          emailId:
            process.env.NODE_ENV === "production" ? rec.emailId : rec.loginid,
          role: userRole?.name || "",
          isAdmin: (
            <Switch
              disabled={rec.userId === loggedInUser.userId || rec.roleId === 7 || rec.emailId === "prashantv@tataprojects.com"}
              checked={isAdminChecked}
              onChange={() => handleIsAdmin(rec.userId, !isAdminChecked)}
            />
          ),
          isImportantEmailReceiver: (
            <Switch
              checked={isImportantEmailReceiverChecked}
              onChange={() => handleIsImportantEmailReceiver(rec.userId, !isImportantEmailReceiverChecked)}
            />
          ),
          createdDate: displayDateFormart(rec.createdDate),
        };
      })
    );
  }, [allData, allRoles, dispatch, loggedInUser]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allData.find((rec) => rec.userId === id);
    setMode("edit");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {isUsersFetching ? <Loader /> : null}
      <PageTitle title="Users" btnText="Add User" onClick={createHandler} />
      <PageSearch placeholder="Search Users" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={mode === "add" ? "Create User" : "Edit User"}
          handleModalClose={handleModalClose}
        >
          <UserForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default UserPage;
